import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { usePermissions } from '../hooks/usePermissions';
import api from '../api/axios';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', text: '#E8EDF5', sub: '#C5CDD8',
};

const STYLES = `
  .mc-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 32px 20px; border-bottom: 1px solid ${C.border};
  }
  .mc-title { font-size: 1.35rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .mc-subtitle { color: ${C.muted}; font-size: 0.82rem; margin-top: 4px; }

  .mc-body {
    padding: 24px 32px;
    display: grid; grid-template-columns: 1fr 300px; gap: 22px; align-items: start;
  }
  @media (max-width: 860px) { .mc-body { grid-template-columns: 1fr; } }

  .mc-card {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; padding: 18px 20px;
    margin-bottom: 16px;
  }
  .mc-card:last-child { margin-bottom: 0; }
  .mc-card-title { font-size: 0.88rem; font-weight: 700; color: ${C.text}; margin-bottom: 4px; }
  .mc-card-sub { font-size: 0.75rem; color: ${C.muted}; margin-bottom: 12px; }

  .mc-label {
    font-size: 0.73rem; font-weight: 600; color: ${C.muted}; text-transform: uppercase;
    letter-spacing: .05em; display: block; margin-bottom: 8px;
  }
  .mc-select {
    padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.text}; font-size: 0.83rem; outline: none;
    transition: border-color .15s; max-width: 280px; width: 100%; box-sizing: border-box;
    font-family: inherit;
  }
  .mc-select:focus { border-color: ${C.teal}; }
  select.mc-select option { background: ${C.surface}; }

  .mc-error {
    padding: 10px 14px; border-radius: 9px; margin-bottom: 14px;
    background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3);
    color: ${C.coral}; font-size: 0.82rem;
  }
  .mc-success {
    padding: 10px 14px; border-radius: 9px; margin-bottom: 14px;
    background: rgba(0,201,177,0.1); border: 1px solid rgba(0,201,177,0.25);
    color: ${C.teal}; font-size: 0.82rem; font-weight: 600;
  }
  .mc-loading { padding: 30px; text-align: center; color: ${C.muted}; font-size: 0.85rem; }

  .mc-closed-banner {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; border-radius: 12px; margin-bottom: 16px;
    background: rgba(56,189,248,0.06); border: 1px solid rgba(56,189,248,0.2);
  }
  .mc-closed-title { font-weight: 700; color: ${C.sky}; margin-bottom: 3px; font-size: 0.88rem; }
  .mc-closed-meta { font-size: 0.75rem; color: ${C.muted}; }

  .mc-kpi-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; margin-bottom: 16px;
  }
  .mc-kpi {
    background: rgba(255,255,255,0.03); border: 1px solid ${C.border}; border-radius: 10px;
    padding: 12px 14px;
  }
  .mc-kpi-label { font-size: 0.68rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 6px; }
  .mc-kpi-value { font-size: 1rem; font-weight: 800; color: ${C.text}; font-family: 'JetBrains Mono', monospace; }
  .mc-kpi-value.positive { color: ${C.teal}; }
  .mc-kpi-value.negative { color: ${C.coral}; }

  .mc-expense-row {
    display: flex; justify-content: space-between; padding: 6px 0;
    border-bottom: 1px solid rgba(42,46,69,0.6); font-size: 0.83rem;
  }
  .mc-expense-row:last-child { border-bottom: none; }
  .mc-expense-cat { color: ${C.sub}; }
  .mc-expense-amt { font-weight: 600; color: ${C.text}; font-family: 'JetBrains Mono', monospace; }

  .mc-checklist { display: flex; flex-direction: column; }
  .mc-check-item {
    display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px solid rgba(42,46,69,0.6);
    align-items: flex-start;
  }
  .mc-check-item:last-child { border-bottom: none; }
  .mc-check-icon {
    flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; font-size: 0.75rem; margin-top: 1px;
  }
  .mc-check-icon.ok { background: rgba(0,201,177,0.12); color: ${C.teal}; }
  .mc-check-icon.warn { background: rgba(240,180,41,0.12); color: ${C.gold}; }
  .mc-check-icon.fail { background: rgba(255,107,107,0.12); color: ${C.coral}; }
  .mc-check-label { font-size: 0.85rem; font-weight: 600; color: ${C.text}; }
  .mc-check-detail { font-size: 0.75rem; color: ${C.muted}; margin-top: 2px; }

  .mc-notes {
    padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.text}; font-size: 0.83rem; outline: none;
    transition: border-color .15s; width: 100%; box-sizing: border-box;
    font-family: inherit; resize: vertical; display: block; margin-bottom: 12px;
  }
  .mc-notes:focus { border-color: ${C.teal}; }
  .mc-close-btn {
    width: 100%; padding: 11px; background: ${C.gold}; border: none; border-radius: 10px;
    color: #0B0E17; font-size: 0.85rem; font-weight: 700; cursor: pointer; transition: opacity .15s;
  }
  .mc-close-btn:hover { opacity: .88; }
  .mc-close-btn:disabled { opacity: .5; cursor: not-allowed; }

  .mc-btn-sm {
    padding: 5px 12px; background: transparent; border: 1px solid ${C.border};
    border-radius: 8px; color: ${C.sub}; font-size: 0.75rem; font-weight: 600;
    cursor: pointer; transition: all .15s; white-space: nowrap; flex-shrink: 0;
  }
  .mc-btn-sm:hover { border-color: ${C.sky}; color: ${C.sky}; }

  .mc-history-title { font-size: 0.88rem; font-weight: 700; color: ${C.text}; margin-bottom: 14px; }
  .mc-history-empty { font-size: 0.82rem; color: ${C.muted}; text-align: center; padding: 20px 0; }
  .mc-history-item {
    padding: 10px 0; border-bottom: 1px solid rgba(42,46,69,0.6); cursor: pointer; transition: opacity .15s;
  }
  .mc-history-item:last-child { border-bottom: none; }
  .mc-history-item:hover { opacity: .8; }
  .mc-history-row { display: flex; justify-content: space-between; align-items: center; }
  .mc-history-month { font-weight: 600; font-size: 0.85rem; color: ${C.text}; }
  .mc-history-badge {
    font-size: 0.68rem; font-weight: 700; border-radius: 20px; padding: 2px 8px;
  }
  .mc-history-badge.closed { background: rgba(56,189,248,0.1); color: ${C.sky}; }
  .mc-history-badge.open { background: rgba(255,255,255,0.06); color: ${C.muted}; }
  .mc-history-np { font-size: 0.75rem; margin-top: 3px; font-weight: 600; }
`;

const fmt = (n) =>
  `GHS ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function monthOptions() {
  const opts = [];
  const now  = new Date();
  for (let i = 1; i <= 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ month: d.getMonth() + 1, year: d.getFullYear(), label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }) });
  }
  return opts;
}

export default function MonthClose() {
  const { role } = usePermissions();
  const isOwner  = role === 'owner';

  const opts = monthOptions();
  const [selected, setSelected]   = useState(opts[0]);
  const [preview, setPreview]     = useState(null);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [closing, setClosing]     = useState(false);
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get('/close');
      setHistory(r.data || []);
    } catch { /* ignore */ }
  }, []);

  const loadPreview = useCallback(async () => {
    if (!selected) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const r = await api.get(`/close/${selected.year}/${selected.month}`);
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleClose = async () => {
    if (!confirm(`Close ${selected.label}? This will snapshot the financials. You can reopen it later if needed.`)) return;
    setClosing(true); setError('');
    try {
      await api.post(`/close/${selected.year}/${selected.month}`, { notes });
      setSuccess(`${selected.label} has been closed.`);
      setNotes('');
      await Promise.all([loadPreview(), loadHistory()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to close month');
    } finally {
      setClosing(false);
    }
  };

  const handleReopen = async (rec) => {
    if (!confirm(`Reopen ${new Date(rec.year, rec.month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })}?`)) return;
    try {
      await api.delete(`/close/${rec.year}/${rec.month}`);
      await Promise.all([loadPreview(), loadHistory()]);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to reopen');
    }
  };

  const s = preview?.summary;
  const r = preview?.record;
  const isClosed = r?.status === 'closed';

  const checklist = s ? [
    { ok: true,                         label: `${s.salesCount} completed sales`, detail: fmt(s.revenue) + ' revenue' },
    { ok: s.unpaidSalesCount === 0,     warn: s.unpaidSalesCount > 0, label: 'Unpaid / partial sales',   detail: s.unpaidSalesCount > 0 ? `${s.unpaidSalesCount} sale(s) with ${fmt(s.unpaidSalesAmount)} balance due` : 'All sales settled' },
    { ok: s.openDebtsCount === 0,       warn: s.openDebtsCount > 0,   label: 'Customer debts',           detail: s.openDebtsCount > 0 ? `${s.openDebtsCount} open debt(s) — ${fmt(s.openDebtsAmount)} outstanding` : 'No open debts' },
    { ok: s.openAPCount === 0,          warn: s.openAPCount > 0,      label: 'Supplier payables (AP)',   detail: s.openAPCount > 0 ? `${s.openAPCount} unpaid purchase order(s) — ${fmt(s.openAPAmount)}` : 'All supplier bills paid' },
  ] : [];

  return (
    <Layout>
      <style>{STYLES}</style>

      <div className="mc-header">
        <div>
          <h1 className="mc-title">Month-end Close</h1>
          <p className="mc-subtitle">Review and lock your monthly financials</p>
        </div>
      </div>

      <div className="mc-body">
        <div>
          <div className="mc-card">
            <label className="mc-label">Select period to review</label>
            <select
              className="mc-select"
              value={`${selected?.year}-${selected?.month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                setSelected(opts.find((o) => o.year === y && o.month === m));
              }}
            >
              {opts.map((o) => <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>{o.label}</option>)}
            </select>
          </div>

          {error   && <div className="mc-error">{error}</div>}
          {success && <div className="mc-success">{success}</div>}

          {loading ? (
            <div className="mc-loading">Loading preview…</div>
          ) : s && (
            <>
              {isClosed && (
                <div className="mc-closed-banner">
                  <div>
                    <div className="mc-closed-title">🔒 Period closed</div>
                    <div className="mc-closed-meta">Closed on {fmtDate(r.closedAt)}{r.notes ? ` · ${r.notes}` : ''}</div>
                  </div>
                  {isOwner && (
                    <button className="mc-btn-sm" onClick={() => handleReopen(r)}>Reopen</button>
                  )}
                </div>
              )}

              <div className="mc-kpi-grid">
                {[
                  { label: 'Revenue',      value: s.revenue,       sub: `${s.salesCount} sales` },
                  { label: 'COGS',         value: s.cogs },
                  { label: 'Gross Profit', value: s.grossProfit,   positive: true },
                  { label: 'Expenses',     value: s.totalExpenses },
                  { label: 'Net Profit',   value: s.netProfit,     positive: true },
                ].map(({ label, value, positive }) => {
                  const num = Number(value || 0);
                  const cls = positive ? (num >= 0 ? 'positive' : 'negative') : '';
                  return (
                    <div key={label} className="mc-kpi">
                      <div className="mc-kpi-label">{label}</div>
                      <div className={`mc-kpi-value${cls ? ` ${cls}` : ''}`}>{fmt(num)}</div>
                    </div>
                  );
                })}
              </div>

              {Object.keys(s.expensesByCategory).length > 0 && (
                <div className="mc-card">
                  <div className="mc-card-title">Expenses by category</div>
                  {Object.entries(s.expensesByCategory).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                    <div key={cat} className="mc-expense-row">
                      <span className="mc-expense-cat">{cat}</span>
                      <span className="mc-expense-amt">{fmt(amt)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mc-card">
                <div className="mc-card-title">Pre-close checklist</div>
                <div className="mc-card-sub">Review these items before closing the period</div>
                <div className="mc-checklist">
                  {checklist.map((item, i) => (
                    <div key={i} className="mc-check-item">
                      <div className={`mc-check-icon ${item.ok ? 'ok' : item.warn ? 'warn' : 'fail'}`}>
                        {item.ok ? '✓' : item.warn ? '!' : '✗'}
                      </div>
                      <div>
                        <div className="mc-check-label">{item.label}</div>
                        {item.detail && <div className="mc-check-detail">{item.detail}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {isOwner && !isClosed && (
                <div className="mc-card">
                  <div className="mc-card-title">Close {selected?.label}</div>
                  <div className="mc-card-sub">
                    Closing the period takes a financial snapshot. No data is deleted or locked — it's a record-keeping action.
                  </div>
                  <textarea
                    className="mc-notes"
                    rows={2}
                    placeholder="Optional closing notes…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                  <button className="mc-close-btn" onClick={handleClose} disabled={closing}>
                    {closing ? 'Closing…' : `Close ${selected?.label}`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mc-card" style={{ marginBottom: 0 }}>
          <div className="mc-history-title">Close history</div>
          {history.length === 0 ? (
            <div className="mc-history-empty">No closed months yet</div>
          ) : (
            history.map((rec) => {
              const label = new Date(rec.year, rec.month - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
              const np    = Number(rec.netProfit);
              return (
                <div
                  key={rec.id}
                  className="mc-history-item"
                  onClick={() => setSelected(opts.find((o) => o.year === rec.year && o.month === rec.month) || { month: rec.month, year: rec.year, label })}
                >
                  <div className="mc-history-row">
                    <div className="mc-history-month">{label}</div>
                    <span className={`mc-history-badge ${rec.status === 'closed' ? 'closed' : 'open'}`}>
                      {rec.status === 'closed' ? '🔒 Closed' : 'Open'}
                    </span>
                  </div>
                  <div className="mc-history-np" style={{ color: np >= 0 ? C.teal : C.coral }}>
                    {np >= 0 ? '+' : ''}{fmt(np)} net profit
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
}
